/**
 * RestoID — the consent screen's two identity blocks.
 * ---------------------------------------------------------------------------
 * "Who is asking" and "who you are", in RestoID's shapes. This replaces three
 * upstream pieces at once, all of them pure presentation:
 *   - the `BrandingHeader` that `@/Layout/LandingPageLayout` renders for Consent
 *     (tenant logo + connect glyph + third-party logo + headline),
 *   - `src/pages/Consent/UnregisteredClientNotice`,
 *   - `src/pages/Consent/UserProfile`.
 *
 * Everything security-relevant in those files is kept verbatim:
 *   - the third-party logo is still loaded with `referrerPolicy="no-referrer"`,
 *     so a client-declared remote asset cannot carry the tenant origin to its
 *     host, and a load failure still falls back to a local glyph instead of
 *     leaving a hole (upstream `isThirdPartyLogoBroken`);
 *   - the CIMD notice still renders `description.unregistered_client_notice`
 *     through `<Trans>` with a `hostname` slot, so the host — the one part of a
 *     self-declared client's identity it cannot forge — stays visually marked
 *     (CIMD draft-02 §8.5);
 *   - the user line still falls back `name → description.user_id`, and the
 *     identifier still falls back `primaryEmail → primaryPhone → username`.
 *
 * Two deliberate differences:
 *   - the TENANT (RestoID) logo and the connect glyph are gone. RestoID's own
 *     mark is the card's identity everywhere else (`BrandHead` on sign-in, the
 *     page chrome); on a consent screen the only question is which app is
 *     asking, and the design reference (`ConsentScreen`) shows exactly one
 *     lockup — the app's. The fallback tile is deliberately NOT given
 *     `alt="app logo"`: the theme layer force-replaces that alt with the RestoID
 *     shield, which is precisely the confusion we are avoiding here.
 *   - the notice is an amber RestoID tile rather than upstream's cream alert
 *     container, which on the dark page read as a hole punched in it.
 */
import { type ConsentInfoResponse } from '@logto/schemas';
import { type Nullable } from '@silverhand/essentials';
import { type ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { C, FONT, Icon } from './restoid';

/** One line of truncated text — org names and e-mails are both unbounded. */
const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type AppHeaderProps = {
  /** Already-resolved headline (upstream: `description.authorize_title`). */
  title: ReactNode;
  /** The client's own logo, theme-resolved by the caller; may be absent. */
  logoUrl?: Nullable<string>;
};

/** Lime app tile + the authorize headline, on one row (design: ConsentScreen). */
export const ConsentAppHeader = ({ title, logoUrl }: AppHeaderProps) => {
  const [isLogoBroken, setIsLogoBroken] = useState(false);
  const showLogo = Boolean(logoUrl) && !isLogoBroken;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <span
        style={{
          flex: '0 0 auto',
          width: 52,
          height: 52,
          borderRadius: 14,
          background: C.tile,
          border: `1px solid ${C.tileLine}`,
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          color: C.brand,
        }}
      >
        {showLogo && logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            referrerPolicy="no-referrer"
            style={{ width: 32, height: 32, objectFit: 'contain' }}
            onError={() => {
              setIsLogoBroken(true);
            }}
          />
        ) : (
          <Icon name="lock" size={24} sw={1.6} />
        )}
      </span>
      <h2
        style={{
          margin: 0,
          minWidth: 0,
          color: C.text,
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 21,
          lineHeight: 1.25,
          letterSpacing: '-0.02em',
          /* The headline carries a client-supplied name — or, for a CIMD client with no
             `client_name`, the bare identifier host, which has no spaces to break on. Keep
             upstream's bound (`BrandingHeader` clamps `.headline` to two lines): break the
             word so it cannot paint outside the card, and clamp so a long name cannot push
             the deny/authorize row off screen. */
          overflowWrap: 'anywhere',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {title}
      </h2>
    </div>
  );
};

/**
 * The standing notice for an unregistered (CIMD) client: everything on the page
 * is self-declared except the host that serves the metadata document, so the
 * host stays on screen and the user is told to read it.
 */
export const ConsentUnregisteredNotice = ({ host }: { host: string }) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 11,
        padding: '13px 15px',
        borderRadius: 12,
        background: C.warningTile,
        border: `1px solid ${C.warningTileLine}`,
      }}
    >
      <span
        style={{
          flex: '0 0 auto',
          display: 'inline-flex',
          marginTop: 1,
          color: C.warning,
        }}
      >
        <Icon name="alert" size={17} />
      </span>
      <p style={{ margin: 0, color: C.sub, font: `400 13px/1.5 ${FONT}` }}>
        <Trans
          components={{
            /* A host runs to 253 characters and is one unbreakable token: wrap it anywhere,
               as upstream's `.host` does, so the identity signal is never clipped. */
            hostname: <span style={{ color: C.text, fontWeight: 600, overflowWrap: 'anywhere' }} />,
          }}
        >
          {t('description.unregistered_client_notice', { host })}
        </Trans>
      </p>
    </div>
  );
};

/** The account the grant would be made against. */
export const ConsentUserRow = ({ user }: { user: ConsentInfoResponse['user'] }) => {
  const { t } = useTranslation();
  const { id, avatar, name, primaryEmail, primaryPhone, username } = user;
  const identifier = primaryEmail ?? primaryPhone ?? username;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        padding: '12px 14px',
        borderRadius: 12,
        background: C.card,
        border: `1px solid ${C.lineSoft}`,
      }}
    >
      {avatar ? (
        <img
          src={avatar}
          alt=""
          style={{
            flex: '0 0 auto',
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <span
          style={{
            flex: '0 0 auto',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: C.tile,
            border: `1px solid ${C.tileLine}`,
            display: 'grid',
            placeItems: 'center',
            color: C.brand,
          }}
        >
          <Icon name="user" size={20} />
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ color: C.text, font: `600 14.5px/1.3 ${FONT}`, ...ellipsis }}>
          {name ?? t('description.user_id', { id })}
        </div>
        {identifier ? (
          <div style={{ marginTop: 2, color: C.sub, font: `400 13px/1.4 ${FONT}`, ...ellipsis }}>
            {identifier}
          </div>
        ) : null}
      </div>
    </div>
  );
};
